import { Stack, Text, PrimaryButton } from "@certn/cascadia";
import {
  Container,
  Layout,
  Card,
  GhostIconButton,
  SuccessTag,
  DropdownMenu,
  MenuItem,
  MenuSection,
  NeutralBadge,
  SecondaryTopNav,
  Tabs,
  TabList,
  Tab,
  TabPanel,
  showToast,
} from "@certn/cascadia-react";
import { Skeleton } from "@certn/cascadia-react/patterns/skeleton/Skeleton";
import {
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
  useResolvedPath,
} from "react-router-dom";
import { useClientPortalAPI, useSubmit } from "@certn/api";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { Formik } from "formik";

import { PageTitle } from "../../../components/page-title/PageTitle";
import { AdjudicationDetails } from "./AdjudicationDetails";
import { AdjudicationChecks } from "./AdjudicationChecks";
import { AdjudicationActivity } from "./AdjudicationActivity";
import { useAdjudicationEdit } from "./hooks/useAdjudicationEdit";
import { SubCategory } from "../types/canadian-criminal-record-types";
import { DeactivateRubricModal } from "../components/DeactivateRubricModal";
import { ActivateRubricModal } from "../components/ActivateRubricModal";
import { EditRubricWarningModal } from "../components/EditRubricWarningModal";

export function AdjudicationPage() {
  const { t } = useTranslation();
  const { adjudicationId } = useParams<{ adjudicationId: string }>();
  const { data, error, isLoading, mutate } = useClientPortalAPI(
    `/api/internal/client-portal/adjudication-rubrics/${adjudicationId}/`
  );
  const { submitActiveStatus, isSubmittingActiveStatus } = useAdjudicationEdit({
    mutate,
    rubricName: data?.name ?? undefined,
  });
  const navigate = useNavigate();
  const [selectedCheckType, setSelectedCheckType] = useState<string | null>(
    null
  );
  const [selectedSubCategory, setSelectedSubCategory] =
    useState<SubCategory | null>(null);
  const [isDeactivateModalOpen, setIsDeactivateModalOpen] = useState(false);
  const [isActivateModalOpen, setIsActivateModalOpen] = useState(false);

  const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);
  const handleDeactivateRubric = () => {
    submitActiveStatus(false);
    setIsDeactivateModalOpen(false);
  };

  const handleActivateRubric = () => {
    submitActiveStatus(true);
    setIsActivateModalOpen(false);
  };

  const { pathname } = useLocation();
  const url = useResolvedPath("").pathname;

  const { submit, isSubmitting } = useSubmit(
    `/api/internal/client-portal/adjudication-rubrics/${adjudicationId}/update-criteria/`,
    {
      throwErrors: true,
      onSuccess: () => {
        showToast(t("Adjudication criteria updated"), { type: "success" });
        setSelectedCheckType(null);
        setSelectedSubCategory(null);
        mutate();
      },
    }
  );

  return (
    <>
      <Formik
        // Add set: true to the ruleset because this is the editing of an existing rubric
        initialValues={{
          criteria: data?.rulesets
            ? data.rulesets.map((ruleset: any) => ({
                ...ruleset,
                set: true,
              }))
            : [],
        }}
        enableReinitialize
        onSubmit={(values) => {
          // Remove 'set' flag before sending to backend
          const cleanedCriteria = values.criteria.map((criterion) => {
            const { set, ...rest } = criterion;
            return rest;
          });

          submit({ criteria: cleanedCriteria });
        }}
      >
        {({ values, resetForm }) => (
          <>
            <Layout
              layout="narrow"
              header={
                <SecondaryTopNav
                  testID="back-button"
                  leftIcon="arrow_back"
                  onleftIconOnPress={() => {
                    if (selectedSubCategory) {
                      // Handle back from subcategory level to check type level
                      setSelectedSubCategory(null);
                    } else if (selectedCheckType) {
                      // Handle back from check type level to main level
                      resetForm();
                      setSelectedCheckType(null);
                      setSelectedSubCategory(null);
                    } else {
                      // Otherwise go back to adjudication list
                      navigate("/adjudication");
                    }
                  }}
                  titleArea={{
                    title: selectedCheckType ? "" : t("Adjudication"),
                  }}
                >
                  {selectedCheckType ? (
                    <PrimaryButton
                      isLoading={isSubmitting}
                      onPress={() => {
                        // Filter to only send the selected check type's criteria
                        const selectedCriteria = values.criteria.find(
                          (c) => c.check_type === selectedCheckType
                        );

                        if (selectedCriteria) {
                          // Remove 'set' flag before sending to backend
                          const { set, ...cleanedCriteria } = selectedCriteria;
                          submit({ criteria: [cleanedCriteria] });
                        }
                      }}
                      testID="save-criteria-button"
                    >
                      {t("Save Rules")}
                    </PrimaryButton>
                  ) : null}
                </SecondaryTopNav>
              }
            >
              <Container layout="wide">
                <Stack gap="space-4">
                  {selectedCheckType ? (
                    // Show only the check detail view
                    <AdjudicationChecks
                      adjudicationData={data}
                      isLoading={isLoading}
                      selectedCheckType={selectedCheckType}
                      onNavigationChange={setSelectedCheckType}
                      selectedSubCategory={selectedSubCategory}
                      onSubCategorySelect={setSelectedSubCategory}
                    />
                  ) : (
                    // Show normal adjudication details view
                    <>
                      <Stack gap="space-2">
                        <PageTitle>{t("Adjudication details")}</PageTitle>
                      </Stack>

                      {isLoading ? (
                        <Card padding="space-4">
                          <Stack
                            gap="space-3"
                            orientation="horizontal"
                            justify="space-between"
                            align="start"
                          >
                            <Stack>
                              <Stack gap="space-5" justify="space-between">
                                <Skeleton width="300px" height="28px" />
                                <Skeleton width="80px" height="24px" />
                              </Stack>
                            </Stack>
                            <Skeleton width="24px" height="24px" />
                          </Stack>
                        </Card>
                      ) : (
                        <Card padding="space-4">
                          <Stack
                            gap="space-3"
                            orientation="horizontal"
                            justify="space-between"
                            align="start"
                          >
                            <Stack>
                              <Stack gap="space-0" justify="space-between">
                                <Text style="global/heading-large">
                                  {data?.name}
                                </Text>
                                {data?.is_active && (
                                  <SuccessTag type="subtle">
                                    {t("Active")}
                                  </SuccessTag>
                                )}
                              </Stack>
                            </Stack>
                            <DropdownMenu
                              triggerButton={
                                <GhostIconButton
                                  icon="more_vert"
                                  size="small"
                                  aria-label={t("More actions")}
                                />
                              }
                              onItemAction={(menuItem) => {
                                switch (menuItem) {
                                  case "change-active-status":
                                    if (data?.is_active) {
                                      setIsDeactivateModalOpen(true);
                                    } else {
                                      setIsActivateModalOpen(true);
                                    }
                                    break;
                                }
                              }}
                              placement="bottom right"
                            >
                              <MenuSection>
                                <MenuItem
                                  id="change-active-status"
                                  testID="change-active-status-menu-item"
                                >
                                  <Text
                                    style="input/text-field"
                                    color={
                                      data?.is_active
                                        ? "text-danger"
                                        : "text-base"
                                    }
                                  >
                                    {data?.is_active
                                      ? t("Deactivate rubric")
                                      : t("Reactivate rubric")}
                                  </Text>
                                </MenuItem>
                              </MenuSection>
                            </DropdownMenu>
                          </Stack>
                        </Card>
                      )}

                      <Tabs
                        testID="adjudication-tabs"
                        selectedKey={pathname}
                        aria-label="adjudication-tabs"
                      >
                        <TabList aria-label="Tabs">
                          <Tab
                            testID="details-tab"
                            id={`${url}`}
                            href={`${url}`}
                          >
                            {t("Details")}
                          </Tab>
                          <Tab
                            testID="checks-tab"
                            id={`${url}/checks`}
                            href={`${url}/checks`}
                          >
                            <Stack
                              orientation="horizontal"
                              align="center"
                              gap="space-2"
                            >
                              {t("Checks")}
                              {data?.checks && data.checks.length > 0 && (
                                <NeutralBadge>
                                  {data.checks.length}
                                </NeutralBadge>
                              )}
                            </Stack>
                          </Tab>
                          <Tab
                            testID="activity-tab"
                            id={`${url}/activity`}
                            href={`${url}/activity`}
                          >
                            {t("Activity")}
                          </Tab>
                        </TabList>
                        <TabPanel id={pathname}>
                          <Routes>
                            <Route
                              path=""
                              element={
                                <AdjudicationDetails
                                  adjudicationData={data}
                                  isLoading={isLoading}
                                  error={error}
                                  mutate={mutate}
                                />
                              }
                            />
                            <Route
                              path="/checks"
                              element={
                                <AdjudicationChecks
                                  adjudicationData={data}
                                  isLoading={isLoading}
                                  selectedCheckType={selectedCheckType}
                                  onNavigationChange={setSelectedCheckType}
                                  selectedSubCategory={selectedSubCategory}
                                  onSubCategorySelect={setSelectedSubCategory}
                                />
                              }
                            />
                            <Route
                              path="/activity"
                              element={
                                <AdjudicationActivity
                                  adjudicationId={adjudicationId}
                                />
                              }
                            />
                          </Routes>
                        </TabPanel>
                      </Tabs>
                    </>
                  )}
                </Stack>
              </Container>
            </Layout>
            <DeactivateRubricModal
              isOpen={isDeactivateModalOpen}
              isLoading={isSubmittingActiveStatus}
              rubricName={data?.name ?? null}
              onCancel={() => setIsDeactivateModalOpen(false)}
              onConfirm={handleDeactivateRubric}
            />
            <ActivateRubricModal
              isOpen={isActivateModalOpen}
              isLoading={isSubmittingActiveStatus}
              rubricName={data?.name ?? null}
              onCancel={() => setIsActivateModalOpen(false)}
              onConfirm={handleActivateRubric}
            />
            <EditRubricWarningModal
              isOpen={isWarningModalOpen}
              onClose={() => setIsWarningModalOpen(false)}
              rubricName={data?.name || ""}
              packageCount={data?.packages?.length ?? 0}
              onConfirm={() => {
                // Filter to only send the selected check type's criteria
                const selectedCriteria = values.criteria.find(
                  (c) => c.check_type === selectedCheckType
                );

                if (selectedCriteria) {
                  // Remove 'set' flag before sending to backend
                  const { set, ...cleanedCriteria } = selectedCriteria;
                  submit({ criteria: [cleanedCriteria] });
                }
              }}
            />
          </>
        )}
      </Formik>
    </>
  );
}
